import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { fetchQuote } from '../Api/QuoteApi';
import { SERVICE_AGREEMENT } from '../_lib/agreement-clauses';
import { commercialProposalContent } from '../_lib/commercial-prepared';
import CommercialProposalSections, { CommercialCover, CommercialFooter, SectionHeading } from './CommercialProposalSections';
import '../styles/commercial-agreement.css';

// Executed (signed) copy of a commercial service agreement: the durable,
// printable artifact. Renders the full proposal from the immutable signed
// snapshot plus the ESIGN signature record. Mirrors the signing page layout
// so the customer's saved PDF matches what they signed.

const formatPacific = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short',
    });
  } catch { return iso; }
};

const CommercialAgreementView = ({ config }) => {
  const { dealId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [printFallback, setPrintFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setQuote(null);
    setError(null);
    setPrintFallback(false);
    fetchQuote(dealId, { context: 'commercial-agreement' })
      .then((data) => {
        if (cancelled) return;
        if (data?.isCommercial !== true) {
          setError('Agreement not found.');
          return;
        }
        setQuote(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.status === 404 ? 'Agreement not found.' : 'Unable to load agreement.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dealId]);

  if (loading) {
    return (
      <div className="cp-loading">
        <img src={config.logo} alt={config.name} />
        <div>Loading your agreement...</div>
      </div>
    );
  }
  if (error || !quote || quote.isCommercial !== true) {
    return (
      <div className="cp-error-page">
        <img src={config.logo} alt={config.name} />
        <h2>{error || 'Agreement not found.'}</h2>
      </div>
    );
  }

  const isSigned = quote.signedAt && quote.signedAgreementSnapshot;
  if (!isSigned) {
    return (
      <div className="cp-error-page">
        <img src={config.logo} alt={config.name} />
        <h2>This agreement has not been signed yet.</h2>
        <p>To sign, open <a href={`/commercial/${dealId}`}>your agreement page</a>.</p>
      </div>
    );
  }

  // A later edit to the live quote cannot alter the executed copy.
  const snap = quote.signedAgreementSnapshot;
  const sig = snap.signatureMeta || {};
  const content = snap.content || commercialProposalContent(SERVICE_AGREEMENT.commercial, snap.prepared);
  const q = {
    propertyName: snap.propertyName ?? '',
    billingContactName: snap.contacts?.billingContactName ?? '',
    billingContactEmail: snap.contacts?.billingContactEmail ?? '',
    billingContactPhone: snap.contacts?.billingContactPhone ?? '',
    onsiteContactName: snap.contacts?.onsiteContactName ?? '',
    onsiteContactEmail: snap.contacts?.onsiteContactEmail ?? '',
    onsiteContactPhone: snap.contacts?.onsiteContactPhone ?? '',
    serviceAddress: snap.serviceAddress ?? '',
    startDate: snap.startDate ?? '',
    propertyNotes: snap.propertyNotes ?? '',
    howHeard: snap.howHeard ?? '',
    terms: snap.terms ?? 'net30',
    pricing: snap.pricing ?? {},
    prepared: snap.prepared ?? null,
    validUntil: snap.validUntil ?? null,
    region: snap.region ?? null,
    regionLabel: snap.regionLabel ?? null,
    regional: snap.regional ?? null,
    authority: snap.authority ?? null,
  };
  const consentText = snap.consentText || SERVICE_AGREEMENT.commercialConsentText;
  const esignDisclosure = snap.esignDisclosure || SERVICE_AGREEMENT.commercialEsignDisclosure;
  const agreementVersion = snap.agreementVersion || sig.agreementVersion || '';
  const vm = {
    signerName: sig.signerName || '',
    signerTitle: sig.signerTitle || '',
    signedAt: sig.signedAtUtc || '',
    ip: sig.ip || '',
  };

  const handlePrint = () => {
    try { window.print(); setPrintFallback(false); }
    catch (err) {
      setPrintFallback(true);
      Sentry.captureMessage('commercial_agreement_print_fallback', { level: 'warning', extra: { error: String(err) } });
    }
  };

  return (
    <div className="cp-root cp-view-only">
      <CommercialCover config={config} q={q} />
      <div className="cp-body">
        <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 8 }}>
          {quote.archiveStatus === 'verified' && (
            <a className="cp-print-btn" href={`/api/quote/commercial-artifact?dealId=${encodeURIComponent(dealId)}`} download>Download verified copy</a>
          )}
          <button type="button" className="cp-print-btn" onClick={handlePrint}>Print / Save PDF</button>
        </div>

        <CommercialProposalSections config={config} q={q} content={content} />

        {/* Service setup captured at signing (from the immutable snapshot). */}
        <section className="cp-section">
          <SectionHeading eyebrow="Service details">Service setup</SectionHeading>
          <div className="cp-setup">
            <div className="cp-setup-card">
              <div className="cp-setup-head">Property and billing</div>
              <div className="cp-setup-field"><div className="cp-setup-k">Property</div><div className="cp-setup-v">{q.propertyName || '(not provided)'}</div></div>
              <div className="cp-setup-field"><div className="cp-setup-k">Billing contact</div><div className="cp-setup-v">{q.billingContactName || ''}</div></div>
              <div className="cp-setup-field"><div className="cp-setup-k">Billing email</div><div className="cp-setup-v">{q.billingContactEmail || ''}</div></div>
              <div className="cp-setup-field"><div className="cp-setup-k">Billing phone</div><div className="cp-setup-v">{q.billingContactPhone || ''}</div></div>
              <div className="cp-setup-field"><div className="cp-setup-k">How did you hear about us?</div><div className="cp-setup-v">{q.howHeard || '(not provided)'}</div></div>
            </div>
            <div className="cp-setup-card">
              <div className="cp-setup-head">On-site and service details</div>
              <div className="cp-setup-field"><div className="cp-setup-k">On-site contact</div><div className="cp-setup-v">{q.onsiteContactName || '(same as billing)'}</div></div>
              <div className="cp-setup-field"><div className="cp-setup-k">On-site email</div><div className="cp-setup-v">{q.onsiteContactEmail || ''}</div></div>
              <div className="cp-setup-field"><div className="cp-setup-k">On-site phone</div><div className="cp-setup-v">{q.onsiteContactPhone || ''}</div></div>
              <div className="cp-setup-field"><div className="cp-setup-k">Requested start date</div><div className="cp-setup-v">{q.startDate || '(to be scheduled)'}</div></div>
              <div className="cp-setup-field"><div className="cp-setup-k">Property notes</div><div className="cp-setup-v">{q.propertyNotes || '(none)'}</div></div>
            </div>
          </div>
        </section>

        <section className="cp-section">
          <SectionHeading eyebrow="Agreed">Service agreement</SectionHeading>
          <div className="cp-agree-box">{consentText}</div>
        </section>

        <section className="cp-section cp-esign-record">
          <SectionHeading eyebrow="Executed">ESIGN signature record</SectionHeading>
          <div className="cp-sig-preview" aria-hidden="true">{vm.signerName}</div>
          <dl className="cp-esign-grid">
            <dt>Signed by</dt><dd>{vm.signerName}{vm.signerTitle ? `, ${vm.signerTitle}` : ''}</dd>
            <dt>On behalf of</dt><dd>{q.propertyName}</dd>
            <dt>Billing contact</dt><dd>{q.billingContactName} &lt;{q.billingContactEmail}&gt;</dd>
            <dt>Signed at</dt><dd>{formatPacific(vm.signedAt)} ({vm.signedAt})</dd>
            <dt>IP address</dt><dd>{vm.ip || '(not recorded)'}</dd>
            <dt>Deal ID</dt><dd>{quote.dealId || dealId}</dd>
            <dt>Agreement version</dt><dd>{agreementVersion || '(unspecified)'}</dd>
          </dl>
          <p className="cp-esign">{esignDisclosure}</p>
        </section>

        {printFallback && (
          <div className="cp-error no-print" role="alert">
            Printing isn't supported in this in-app browser. Open this page in Safari or Chrome and print from there.
          </div>
        )}

        <CommercialFooter config={config} regional={q.regional} />
      </div>
    </div>
  );
};

export default CommercialAgreementView;
